import { getImageLoading, isOrHandleAsExternalUrl } from "../../../helpers";
import { useAnimateFadeIn, useAnimateFadeInToTop } from "../../../animation/animation";
import type { TIconTheme, TIndexableBlock, TLink } from "../../../types";
import { PrimaryButtonLink } from "../../elements/button/button";
import Carousel from "../../elements/carousel/carousel";
import Heading from "../../elements/heading/heading";
import TeaserCard, { TTeaserCardProps } from "../../elements/teaser-card/teaser-card";
import styles from "./card-list-block.module.less";
import { m as motion } from "motion/react";

export type TCardListBlockProps = {
  cards: Array<TTeaserCardProps>;
  heading?: string;
  text?: string;
  link?: TLink;
  isCarousel?: boolean;
  theme?: "beige";
  cardFallbackImage?: string;
  topIconTheme?: TIconTheme;
} & TIndexableBlock;

export const CardListBlock = ({
  heading,
  text,
  link,
  cards,
  isCarousel,
  cardFallbackImage,
  theme,
  topIconTheme = "standard",
  index: blockIndex,
}: TCardListBlockProps) => {
  const fadeInAnimation = useAnimateFadeIn({ whileInView: true, easing: "easeInOut" });
  const fadeInToTopAnimation = useAnimateFadeInToTop({ whileInView: true, delay: 0.2, easing: "easeOut" });

  const doesAnyCardHaveImage = cards.some((card) => card.image?.url);
  const cardsWithFallback = doesAnyCardHaveImage
    ? cards.map((card) => {
        if (!card.image?.url && cardFallbackImage) {
          return {
            ...card,
            image: { url: cardFallbackImage },
          };
        }
        return card;
      })
    : cards;

  const fadeInAnimationProps = isCarousel ? fadeInAnimation : undefined;

  const cardSemanticHeadingType = heading ? "h3" : "h2";

  return (
    <section className={[styles.wrapper, theme ? `theme-${theme} ${styles.hasBackground}` : ""].join(" ")}>
      <motion.div {...fadeInAnimationProps} className={[styles.innerWrapper, "max-width padded-sides"].join(" ")}>
        {(heading || text) && (
          <div className={styles.textWrapper}>
            {heading && (
              <Heading
                index={blockIndex}
                semanticType={"h2"}
                visualType={"h2"}
                className={[styles.heading, "heading-max-width"].join(" ")}
              >
                {heading}
              </Heading>
            )}
            {text && <p className={[styles.text, "body-base mb-0", "text-max-width"].join(" ")}>{text}</p>}
          </div>
        )}

        {isCarousel ? (
          <Carousel
            items={cardsWithFallback.map((card, index) => (
              <TeaserCard
                key={index}
                {...card}
                semanticHeadingType={cardSemanticHeadingType}
                topIconTheme={topIconTheme}
                imageLoading={getImageLoading(blockIndex)}
              />
            ))}
          />
        ) : (
          <ul className={`${styles.list}`}>
            {cardsWithFallback.map((card, index) => {
              return (
                <li key={index} className={styles.item}>
                  <TeaserCard
                    {...card}
                    semanticHeadingType={cardSemanticHeadingType}
                    topIconTheme={topIconTheme}
                    imageLoading={getImageLoading(blockIndex)}
                    animation={fadeInAnimation}
                  />
                </li>
              );
            })}
          </ul>
        )}

        {link && (
          <motion.div {...fadeInToTopAnimation} className={styles.linkWrapper}>
            {
              <PrimaryButtonLink
                label={link.title}
                url={link.url}
                iconRight={link.icon ?? "ui-icon-arrow-right"}
                openInNewTab={isOrHandleAsExternalUrl(link.url, link.target)}
                onPreNavigate={link.onPreNavigate}
              />
            }
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default CardListBlock;
